import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, MenuItem, Button, Alert, CircularProgress,
  Paper, Divider, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import { CAJA_TECNICA_COLOR } from "../theme";
import { esRolAdminGerenteContable } from "../permisos";
import { fieldSx as compactFieldSx } from "../../egresos/components/formStyles";

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  "", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// whiteSpace: "nowrap" evita que un header de 2 palabras ("Destinado a") se parta en 2
// líneas — con stickyHeader, una celda de encabezado más alta que el resto rompe la
// alineación y el header sticky queda superpuesto con las filas al hacer scroll.
const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "3px 6px",
  whiteSpace: "nowrap",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : CAJA_TECNICA_COLOR,
  // Opaco (no `${CAJA_TECNICA_COLOR}14`, que es semi-transparente): con stickyHeader,
  // un fondo transparente deja ver las filas de abajo a través del header al hacer
  // scroll.
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eff1f1",
}));

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Montos como los mostraba el PHP (number_format): 1,500.00
const monto = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnioISO = () => `${new Date().getFullYear()}-01-01`;

const initialForm = { fein: inicioAnioISO(), fefin: hoyISO(), servicio: "General", sucursal: "" };

const PDF_POR_TIPO = {
  general: { path: "/api/caja-tecnica/reportes/general/pdf", filename: "reporte_caja_tecnica.pdf" },
  porServicio: { path: "/api/caja-tecnica/reportes/general/pdf", filename: "reporte_caja_tecnica.pdf" },
  resumen: { path: "/api/caja-tecnica/reportes/resumen/pdf", filename: "resumen_caja_tecnica.pdf" },
  porMes: { path: "/api/caja-tecnica/reportes/por-mes/pdf", filename: "reporte_por_mes_caja_tecnica.pdf" },
};

const NOMBRE_CAJA = "Caja Técnica";
const NOMBRE_REPORTE = { general: "Reporte Total", porServicio: "Reporte Por Servicio", porMes: "Reporte Por Mes", resumen: "Resumen Total" };

const downloadBlob = (data, contentType, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const TablaGeneral = ({ data }) => (
  <>
    <TableContainer>
      <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
        <TableHead>
          <TableRow>
            <Th>Fecha</Th>
            <Th>Concepto</Th>
            <Th>Destinado a</Th>
            <Th>Descripción</Th>
            <Th>Proveedor</Th>
            <Th>Servicio</Th>
            <Th>Factura o Recibo</Th>
            <Th>Número de Egreso</Th>
            <Th align="right">Ingreso</Th>
            <Th align="right">Egreso</Th>
            <Th align="right">Saldo</Th>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.items.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{formatFecha(r.fecha)}</TableCell>
              <TableCell>{r.item_descripcion}</TableCell>
              <TableCell>{r.gasto || "-"}</TableCell>
              <TableCell>{r.descripcion || "-"}</TableCell>
              <TableCell>{r.proveedor || "-"}</TableCell>
              <TableCell>{r.servicio || "-"}</TableCell>
              <TableCell>{r.num_fac || ""}</TableCell>
              <TableCell>{r.num_recibo || ""}</TableCell>
              <TableCell align="right">{r.tipo === "Ingreso" ? monto(r.monto) : "0.00"}</TableCell>
              <TableCell align="right">{r.tipo === "Egreso" ? monto(r.monto) : "0.00"}</TableCell>
              <TableCell align="right">{monto(r.saldo)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Stack sx={{ mt: 1.5 }} spacing={0.3}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
        Total Ingreso: <span style={{ color: CAJA_TECNICA_COLOR }}>{monto(data.totalIngreso)} Bs.</span>
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
        Total Egreso: {monto(data.totalEgreso)} Bs.
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
        Saldo Actual: {monto(data.saldoActual)} Bs.
      </Typography>
    </Stack>
  </>
);

const TablaDesglose = ({ titulo, filas, campo }) => (
  <TableContainer>
    <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
      <TableHead>
        <TableRow>
          <Th>{titulo}</Th>
          <Th align="right">Total</Th>
        </TableRow>
      </TableHead>
      <TableBody>
        {filas.length === 0 ? (
          <TableRow><TableCell colSpan={2}>No existen registros</TableCell></TableRow>
        ) : filas.map((r, i) => (
          <TableRow key={i} hover>
            <TableCell>{r[campo] || "-"}</TableCell>
            <TableCell align="right">{monto(r.sumado)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TablaResumen = ({ data }) => (
  <>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
      <TablaDesglose titulo="Concepto" filas={data.porItem} campo="descripcion" />
      <TablaDesglose titulo="Servicio" filas={data.porServicio} campo="servicio" />
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
      <TablaDesglose titulo="Destinado a" filas={data.porGasto} campo="gasto" />
    </Box>
    <Stack sx={{ mt: 1.5 }} spacing={0.3}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>Total: {monto(data.total)} Bs.</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>Saldo Actual: {monto(data.saldoActual)} Bs.</Typography>
    </Stack>
  </>
);

const TablaPorMes = ({ data }) => (
  <>
    {data.meses.length === 0 ? (
      <Alert severity="info">No existen registros para el período seleccionado.</Alert>
    ) : data.meses.map((m) => (
      <Paper key={`${m.anio}-${m.mes}`} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, mb: 1, color: CAJA_TECNICA_COLOR }}>
          {MESES[m.mes]} {m.anio}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
          <TablaDesglose titulo="Concepto" filas={m.porItem} campo="descripcion" />
          <TablaDesglose titulo="Servicio" filas={m.porServicio} campo="servicio" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <TablaDesglose titulo="Destinado a" filas={m.porGasto} campo="gasto" />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", mt: 1 }}>
          Subtotal del mes: {monto(m.subtotal)} Bs.
        </Typography>
      </Paper>
    ))}
    <Stack spacing={0.3}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
        Total del Período: {monto(data.totalPeriodo)} Bs.
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
        Saldo Actual: {monto(data.saldoActual)} Bs.
      </Typography>
    </Stack>
  </>
);

const ReportesTab = () => {
  const { user } = useAuth();
  const puedeElegirSucursal = esRolAdminGerenteContable(user);

  const [form, setForm] = useState(initialForm);
  const [servicios, setServicios] = useState([]);
  const [tipoReporte, setTipoReporte] = useState(null); // 'general' | 'porServicio' | 'resumen' | 'porMes'
  const [data, setData] = useState(null);
  const [consulta, setConsulta] = useState(null); // filtros con los que se generó el reporte que está en pantalla
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState("");

  // Precarga la sucursal propia del usuario en el filtro — solo aplica a quien puede
  // elegir sucursal; igual puede cambiarla a otra. Ya no hace falta una opción "Mi
  // sucursal" en blanco: seleccionar la propia por nombre da el mismo resultado.
  useEffect(() => {
    if (puedeElegirSucursal && user?.sucursal) {
      setForm((prev) => (prev.sucursal ? prev : { ...prev, sucursal: user.sucursal }));
    }
  }, [puedeElegirSucursal, user]);

  useEffect(() => {
    axios.get("/api/caja-tecnica/servicios")
      .then((res) => setServicios(res.data?.data || []))
      .catch(() => {});
  }, []);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const paramsBase = useCallback(() => {
    const params = { fein: form.fein, fefin: form.fefin };
    if (puedeElegirSucursal && form.sucursal) params.sucursal = form.sucursal;
    return params;
  }, [form, puedeElegirSucursal]);

  const ejecutar = async (tipo) => {
    if (tipo === "porServicio" && !form.servicio) {
      setError("Selecciona un servicio para este reporte.");
      return;
    }
    setLoading(true);
    setError("");
    setTipoReporte(tipo);
    setData(null);
    try {
      let res;
      if (tipo === "general" || tipo === "porServicio") {
        const params = paramsBase();
        if (tipo === "porServicio") params.servicio = form.servicio;
        res = await axios.get("/api/caja-tecnica/reportes/general", { params });
      } else if (tipo === "resumen") {
        res = await axios.get("/api/caja-tecnica/reportes/resumen", { params: paramsBase() });
      } else {
        res = await axios.get("/api/caja-tecnica/reportes/por-mes", { params: paramsBase() });
      }
      setData(res.data?.data || null);
      setConsulta({ tipo, fein: form.fein, fefin: form.fefin, servicio: tipo === "porServicio" ? form.servicio : "", sucursal: res.data?.data?.sucursal || "" });
    } catch (e) {
      setError(e?.response?.data?.message || "Error al generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const exportarPdf = async () => {
    const { path, filename } = PDF_POR_TIPO[tipoReporte];
    setExportando(true);
    setError("");
    try {
      // Con los filtros del reporte que se ve en pantalla, aunque después se hayan cambiado en el formulario.
      const params = { fein: consulta.fein, fefin: consulta.fefin };
      if (consulta.sucursal) params.sucursal = consulta.sucursal;
      if (tipoReporte === "porServicio") params.servicio = consulta.servicio;
      const res = await axios.get(path, { params, responseType: "blob" });
      downloadBlob(res.data, "application/pdf", filename);
    } catch (e) {
      setError("No se pudo exportar el reporte a PDF.");
    } finally {
      setExportando(false);
    }
  };

  const puedeGenerar = form.fein && form.fefin && !loading;
  const sinResultados =
    data && ((tipoReporte === "general" || tipoReporte === "porServicio") && data.items.length === 0);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Reportes de Caja Técnica
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Stack spacing={0.75}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: puedeElegirSucursal ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr" },
              gap: 1.5,
            }}
          >
            <TextField
              size="small" type="date" label="Fecha Inicio" required
              InputLabelProps={{ shrink: true }}
              value={form.fein} onChange={handleChange("fein")}
              sx={compactFieldSx}
            />
            <TextField
              size="small" type="date" label="Fecha Fin" required
              InputLabelProps={{ shrink: true }}
              value={form.fefin} onChange={handleChange("fefin")}
              sx={compactFieldSx}
            />
            <TextField
              size="small" select label="Servicio"
              value={form.servicio} onChange={handleChange("servicio")}
              sx={compactFieldSx}
            >
              {servicios.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            {puedeElegirSucursal && (
              <TextField
                size="small" select label="Sucursal"
                value={form.sucursal} onChange={handleChange("sucursal")}
                sx={compactFieldSx}
              >
                {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {[
                  { tipo: "general", label: "Reporte Total" },
                  { tipo: "porServicio", label: "Reporte Por Servicio" },
                  { tipo: "porMes", label: "Reporte Por Mes" },
                  { tipo: "resumen", label: "Resumen Total" },
                ].map(({ tipo, label }) => (
                  <TableRow key={tipo}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>{label}</TableCell>
                    <TableCell align="center" sx={{ width: 180 }}>
                      <Button
                        size="small" variant="contained" fullWidth
                        disabled={!puedeGenerar} onClick={() => ejecutar(tipo)}
                        sx={{ bgcolor: CAJA_TECNICA_COLOR, "&:hover": { bgcolor: "#20292e" } }}
                      >
                        Enviar Consulta
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
      )}

      {!loading && data && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: CAJA_TECNICA_COLOR }}>
                Reporte {NOMBRE_CAJA} de {consulta?.fein} hasta {consulta?.fefin}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {NOMBRE_REPORTE[tipoReporte]}{data.sucursal ? ` · Sucursal: ${data.sucursal}` : ""}{consulta?.servicio ? ` · ${consulta.servicio}` : ""}
              </Typography>
            </Box>
            <Button
              size="small" startIcon={<PictureAsPdfIcon />} disabled={exportando}
              onClick={exportarPdf} sx={{ color: CAJA_TECNICA_COLOR }}
            >
              {exportando ? "Generando..." : "Exportar PDF"}
            </Button>
          </Box>
          <Divider sx={{ mb: 1.5 }} />

          {sinResultados ? (
            <Alert severity="info">No existen registros para el período seleccionado.</Alert>
          ) : (
            <>
              {(tipoReporte === "general" || tipoReporte === "porServicio") && <TablaGeneral data={data} />}
              {tipoReporte === "resumen" && <TablaResumen data={data} />}
              {tipoReporte === "porMes" && <TablaPorMes data={data} />}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ReportesTab;
